'use client';

import * as React from 'react';
import { Info, LoaderCircle } from 'lucide-react';
import { Toaster as Sonner } from 'sonner';

const WarnIcon = () => (
    <svg className="mr-2 mb-[3px] shrink-0" viewBox="0 0 1024 1024" width="16" height="16">
        <path d="M1001.661867 796.544c48.896 84.906667 7.68 157.013333-87.552 157.013333H110.781867c-97.834667 0-139.050667-69.504-90.112-157.013333l401.664-666.88c48.896-87.552 128.725333-87.552 177.664 0l401.664 666.88zM479.165867 296.533333v341.333334a32 32 0 1 0 64 0v-341.333334a32 32 0 1 0-64 0z m0 469.333334v42.666666a32 32 0 1 0 64 0v-42.666666a32 32 0 1 0-64 0z" fill="#FAAD14"></path>
    </svg>
);

const ErrorIcon = () => (
    <svg className="mr-[6px] shrink-0" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="8" cy="8" r="8" fill="#F0262D" />
        <rect x="7" y="3.5" width="2" height="5" rx="1" fill="#FFFFFF" />
        <circle cx="8" cy="11.25" r="1.25" fill="#FFFFFF" />
    </svg>
);

const SuccessIcon = () => (
    <svg className="mr-2 mb-[2px] shrink-0" width="16" height="16" viewBox="0 0 12 12">
        <rect fill="#00CC41" x="0" y="0" width="12" height="12" rx="6"></rect>
        <path d="M4.84779102,7.16730218 L3.69480695,6.00672178 C3.55903653,5.86190038 3.35580594,5.80284906 3.16427787,5.8525696 C2.9727498,5.90229013 2.82321168,6.05291992 2.77391181,6.24578543 C2.72461195,6.43865094 2.78334642,6.64325323 2.92723688,6.77989533 L4.4633544,8.32657045 C4.67512033,8.53979183 5.01915853,8.53979183 5.23092447,8.32657045 L9.07007799,4.46070273 C9.21550754,4.3244613 9.27546136,4.11914619 9.22641104,3.92533163 C9.17736073,3.73151706 9.02713277,3.58012814 8.83468289,3.5305762 C8.64223302,3.48102426 8.43826856,3.54121582 8.30283371,3.68752919 L4.84713943,7.16631808 Z" fill="#FFFFFF" fillRule="nonzero"></path>
    </svg>
);

type ToasterProps = React.ComponentProps<typeof Sonner>;

const Toaster = ({ ...props }: ToasterProps) => {
    return (
        <Sonner
            className="toaster group"
            position="top-center" // 对应 toastUtils 的 top: 30px
            icons={{
                success: <SuccessIcon />,
                warning: <WarnIcon />,
                error: <ErrorIcon />,
                info: <Info className="h-4 w-4 shrink-0 mr-2 text-[#36B2FF]" />,
                loading: <LoaderCircle className="h-4 w-4 shrink-0 mr-2 animate-spin text-[#36B2FF]" />,
            }}
            toastOptions={{
                classNames: {
                    // 全局基础样式覆盖
                    toast: '!w-fit !max-w-[400px] !justify-center !px-[15px] !py-[5px] !text-[14px] !rounded-[8px] !mx-auto !inset-x-0',
                    // 根据不同的类型指定前景色与背景色
                    success: '!bg-[#DBFFE7] !text-[#3B3B3B]',
                    error: '!bg-[#FFE3E0] !text-[#3B3B3B]',
                    warning: '!bg-[#FFECC7] !text-[#3B3B3B]',
                    info: '!bg-[#ffffff] !text-[#333333]',
                    loading: '!bg-[#ffffff] !text-[#333333]', // loading 使用白色背景
                    // 如果不是特定的 state，默认样式
                    default: '!bg-[#ffffff] !text-[#333333]',
                    title: '!font-normal !ml-0', // 取消粗体，去掉多余的 margin
                    description: '!text-inherit opacity-80',
                },
            }}
            {...props}
        />
    );
};

export { Toaster };